/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * About page — identity, pinned 3D career journey, and LinkedIn-backed resume.
 */

import { createFileRoute } from '@tanstack/react-router';
import {
  Box,
  Container,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import { AboutJourney } from '@/components/AboutJourney';
// ⚡ Path-based imports (6× faster than barrel imports)
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AwardIcon from '@mui/icons-material/EmojiEvents';
import CodeIcon from '@mui/icons-material/Code';
import LanguageIcon from '@mui/icons-material/Language';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SportsIcon from '@mui/icons-material/SportsFootball';
import linkedinData from '@/data/linkedin-profile.json';
import { pageColors, getGradient } from '@/theme/colors';
import { useThemeContext } from '@/contexts/ThemeContext';
import { createPageMeta, PAGE_KEYWORDS } from '@/lib/meta';
import { parseJourneyDemoSearch } from '@/lib/journeyDemo';

export const Route = createFileRoute('/about')({
  component: AboutPage,
  validateSearch: parseJourneyDemoSearch,
  head: () => {
    const { profile } = linkedinData;
    const yearsExperience = new Date().getFullYear() - 2012;
    const base = createPageMeta({
      title: `About ${profile.name} - CTO, Software Architect, Open Source Contributor`,
      description: `${profile.headline}. ${yearsExperience}+ years building scalable systems for startups and enterprise. Expertise in React, Node.js, Python, AWS, and infrastructure as code.`,
      path: '/about',
      image: '/og/about.png',
      type: 'profile',
      keywords: PAGE_KEYWORDS.about,
    });

    return {
      meta: [
        ...base.meta,
        { property: 'profile:first_name', content: profile.name.split(' ')[0] },
        { property: 'profile:last_name', content: profile.name.split(' ')[1] },
      ],
      links: base.links,
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            '@id': 'https://cdbattaglia.com/#person',
            name: profile.name,
            alternateName: ['Chris Battaglia', 'cdbattags'],
            jobTitle: linkedinData.experience.find(e => e.current)?.title || 'Chief Technology Officer',
            description: profile.summary?.split('\n\n')[0] || profile.headline,
            worksFor: {
              '@type': 'Organization',
              name: linkedinData.experience.find(e => e.current)?.company || 'Slabstack',
              description: 'A Sysdyne Company',
              url: 'https://slabstack.com',
            },
            alumniOf: linkedinData.education.map(edu => ({
              '@type': 'CollegeOrUniversity',
              name: edu.school,
              sameAs: edu.school.includes('Georgia') ? 'https://www.gatech.edu/' : undefined,
            })),
            address: {
              '@type': 'PostalAddress',
              addressLocality: profile.location?.split(',')[0]?.trim() || 'Brooklyn',
              addressRegion: profile.location?.split(',')[1]?.trim() || 'NY',
              addressCountry: 'US',
            },
            image: 'https://cdbattaglia.com/assets-cdbattaglia/media/images/og-homepage.png',
            url: 'https://cdbattaglia.com',
            sameAs: [
              'https://github.com/cdbattags',
              'https://www.linkedin.com/in/cdbattags/',
              'https://twitter.com/cdbattags',
              'https://unsplash.com/@cdbattags',
              'https://open.spotify.com/user/cdbattaglia',
            ],
            knowsLanguage: linkedinData.languages.map(lang => ({
              '@type': 'Language',
              name: lang.name,
              alternateName: lang.name.toLowerCase().substring(0, 2),
            })),
            hasOccupation: [
              {
                '@type': 'Occupation',
                name: linkedinData.experience.find(e => e.current)?.title || 'Chief Technology Officer',
                occupationLocation: {
                  '@type': 'City',
                  name: profile.location?.split(',')[0]?.trim() || 'New York',
                },
                skills: linkedinData.skills.slice(0, 20).map(s => s.name),
              },
            ],
            knowsAbout: [
              'Product Engineering',
              'Engineering Leadership',
              'Organizational Design',
              'Full-Stack Development',
              'Software Architecture',
              'Infrastructure as Code',
              'DevOps',
              'Music Technology',
              'Audio Analysis',
              'Developer Tools',
              'Open Source',
              'System Scalability',
              'Team Leadership',
            ],
          }),
        },
      ],
    };
  },
});

// Helper to format LinkedIn dates
function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Present';

  // Handle formats: "YYYY-MM-DD", "YYYY-MM", "MMM YYYY"
  if (dateStr.includes('-')) {
    const [year, month] = dateStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return month ? `${monthNames[parseInt(month) - 1]} ${year}` : year;
  }

  return dateStr; // Already formatted like "Oct 2016"
}

// Convert date string to comparable number for sorting
function dateToNumber(dateStr: string | null): number {
  if (!dateStr) return 999999; // Current roles go first

  const monthMap: Record<string, string> = {
    'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
    'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
  };

  // Handle "YYYY-MM-DD" or "YYYY-MM"
  if (dateStr.includes('-')) {
    const [year, month] = dateStr.split('-');
    return parseInt(year) * 100 + (month ? parseInt(month) : 0);
  }

  // Handle "MMM YYYY" format
  const match = dateStr.match(/(\w+)\s+(\d{4})/);
  if (match) {
    const [, month, year] = match;
    const monthNum = monthMap[month] || '00';
    return parseInt(year) * 100 + parseInt(monthNum);
  }

  // Fallback: try to parse as year only
  const yearMatch = dateStr.match(/\d{4}/);
  return yearMatch ? parseInt(yearMatch[0]) * 100 : 0;
}

// Parse description bullets (split on " - ")
function parseDescriptionBullets(description: string): { bullets: string[]; techStack: string[] } {
  if (!description) return { bullets: [], techStack: [] };

  // Split description from tech environment
  const parts = description.split(/Technical environment:/i);
  const mainDescription = parts[0] || '';
  const techEnvironment = parts[1] || '';

  // Parse bullets (split on " - " and clean up)
  const bullets = mainDescription
    .split(' - ')
    .map(bullet => bullet.trim().replace(/^-\s*/, '')) // Remove leading "- " if present
    .filter(bullet => bullet.length > 10 && bullet.length < 500); // Filter out empty/short and overly long

  // Parse tech stack
  const techStack = techEnvironment
    .split(',')
    .map(tech => tech.trim())
    .filter(tech => tech.length > 0 && tech.length < 30) // Filter out long phrases
    .slice(0, 8); // Limit to 8 items

  return { bullets, techStack };
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\+\+/g, 'plusplus')
    .replace(/\+/g, 'plus')
    .replace(/#/g, 'sharp')
    .replace(/\./g, 'dot')
    .replace(/&/g, 'and')
    .replace(/@/g, 'at')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function AboutPage() {
  const { demo } = Route.useSearch();
  const { mode } = useThemeContext();
  if (demo === 'journey') {
    return <AboutJourney />;
  }

  const { profile, experience: linkedinExp, education: linkedinEdu, skills: linkedinSkills, languages } = linkedinData;

  // Transform LinkedIn experience to component format (all roles, most recent first)
  const experience = linkedinExp
    .map(exp => {
      const { bullets, techStack } = parseDescriptionBullets(exp.description || '');
      return {
        role: exp.title,
        company: exp.company,
        period: `${formatDate(exp.startDate)} - ${formatDate(exp.endDate)}`,
        location: exp.location || '',
        bullets: bullets,
        current: exp.current,
        highlights: techStack,
        _sortDate: dateToNumber(exp.startDate), // For sorting
      };
    })
    .sort((a, b) => b._sortDate - a._sortDate); // Most recent first

  // Group skills by category
  const skillCategories = {
    'Languages': ['JavaScript', 'TypeScript', 'Python', 'Java', 'Rust', 'PHP', 'Ruby', 'C#', 'Swift'],
    'Frontend': ['React.js', 'Vue.js', 'AngularJS', 'Redux.js', 'Express.js', 'SASS'],
    'Backend': ['Node.js', 'Spring MVC', 'Dropwizard', 'Express.js'],
    'Infrastructure': ['Amazon Web Services (AWS)', 'Microsoft Azure', 'Docker Products', 'Kubernetes', 'Terraform', 'DevOps'],
    'Databases': ['PostgreSQL', 'Postgres', 'MongoDB', 'Redis', 'MySQL', 'MariaDB', 'SQL'],
    'Tools & Platforms': ['Git', 'GitHub', 'Linux', 'Azure DevOps', 'Microservices'],
  };

  const skills = Object.entries(skillCategories).map(([category, categorySkills]) => ({
    category,
    items: linkedinSkills
      .filter(skill => categorySkills.includes(skill.name))
      .map(skill => skill.name.replace(/\.js$/, '').replace(/ \(.+\)$/, '')) // Clean names
      .slice(0, 8),
  })).filter(group => group.items.length > 0);

  return (
    <Box sx={{ minHeight: 'calc(100dvh - var(--site-header-height))' }}>
      <AboutJourney />

      {/* Hero */}
      <Box
        id="summary"
        sx={{
          py: { xs: 6, md: 10 },
          background: getGradient(pageColors.about, mode),
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                {/* Avatar & Name */}
                <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
                  <Avatar
                    src="/assets-cdbattaglia/media/images/chris_profile.svg"
                    alt="Christian Battaglia"
                    sx={{
                      width: 140,
                      height: 140,
                      border: '4px solid white',
                      boxShadow: 3,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 400,
                        opacity: 0.8,
                        letterSpacing: '0.5px',
                      }}
                    >
                      about
                    </Typography>
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        fontWeight: 900,
                        lineHeight: 1.2,
                      }}
                    >
                      {profile.name}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                      @cdbattags
                    </Typography>
                  </Box>
                </Stack>

                {/* Quick Stats Card */}
                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        Quick Stats
                      </Typography>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {new Date().getFullYear() - 2012}+ years
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Building software
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {linkedinExp.length} positions
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Across startups & enterprise
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {profile.location?.split(',')[0]}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          {profile.location?.split(',').slice(1).join(',')}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                {/* Headline - Parsed into Role Cards */}
                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.8, fontWeight: 600, letterSpacing: '1px', display: 'block', mb: 1.5 }}>
                    Career Journey
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {profile.headline.split(' | ').map((role, idx) => {
                      // Parse role into title and company
                      const parts = role.split(' @ ');
                      const title = parts[0]?.trim();
                      const company = parts[1]?.trim();

                      // Determine if this is a key role (CTO, Founder)
                      const isKeyRole = title?.toLowerCase().includes('cto') || title?.toLowerCase().includes('founder');

                      return (
                        <Chip
                          key={idx}
                          label={
                            <Box sx={{ py: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                {title}
                              </Typography>
                              {company && (
                                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                                  {company}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{
                            height: 'auto',
                            py: 1,
                            px: 2,
                            bgcolor: isKeyRole ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(10px)',
                            border: isKeyRole ? '2px solid rgba(255, 255, 255, 0.4)' : '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            '& .MuiChip-label': {
                              px: 0,
                            },
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>

                {/* Summary */}
                <Box>
                  {profile.summary?.split('\n\n').map((paragraph, idx) => (
                    <Typography
                      key={idx}
                      variant="body1"
                      sx={{
                        fontSize: '1.1rem',
                        lineHeight: 1.8,
                        opacity: 0.95,
                        mb: paragraph.startsWith('-') ? 0 : 2,
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Stack spacing={8}>
          {/* Experience */}
          <Box id="experience" sx={{ scrollMarginTop: 'calc(var(--site-header-height) + 16px)' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
              <WorkIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h3" fontWeight="bold">
                Experience
              </Typography>
            </Stack>

            <Stack spacing={3}>
              {experience.map((exp, index) => (
                <Card key={index} id={`exp-${slugify(exp.company)}-${slugify(exp.role)}`} sx={{ scrollMarginTop: 'calc(var(--site-header-height) + 16px)', border: exp.current ? '2px solid' : 'none', borderColor: 'primary.main' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 9 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6" fontWeight="bold">
                              {exp.role}
                            </Typography>
                            {exp.current && (
                              <Chip label="Current" color="primary" size="small" sx={{ fontWeight: 600 }} />
                            )}
                          </Stack>
                          <Typography variant="body1" color="primary.main" fontWeight="600">
                            {exp.company}
                          </Typography>
                          {exp.bullets && exp.bullets.length > 0 && (
                            <Box
                              component="ul"
                              sx={{
                                mt: 1.5,
                                mb: 1.5,
                                pl: 3,
                                listStyleType: 'disc',
                                '& li': {
                                  display: 'list-item',
                                  mb: 0.75,
                                }
                              }}
                            >
                              {exp.bullets.map((bullet, idx) => (
                                <Box component="li" key={idx}>
                                  <Typography variant="body2" color="text.secondary">
                                    {bullet}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                          {exp.highlights && exp.highlights.length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, mt: 1 }}>
                              {exp.highlights.map((highlight) => (
                                <Chip key={highlight} label={highlight} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Stack spacing={0.5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {exp.period}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {exp.location}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>

          {/* Skills */}
          <Box id="skills">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
              <CodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h3" fontWeight="bold">
                Skills & Technologies
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {skills.map((skillGroup) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={skillGroup.category}>
                  <Paper sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {skillGroup.category}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {skillGroup.items.map((item) => (
                        <Chip key={item} label={item} size="small" />
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Education & More */}
          <Grid id="education" container spacing={4}>
            {/* Education */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <SchoolIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                      <Typography variant="h5" fontWeight="bold">
                        Education
                      </Typography>
                    </Stack>
                    <Divider />
                    {linkedinEdu.map((edu, index) => (
                      <Box key={index}>
                        <Typography variant="h6" fontWeight="bold">
                          {edu.school}
                        </Typography>
                        <Typography variant="body1" color="primary.main">
                          {edu.degree} {edu.field && `- ${edu.field}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(edu.startDate)} - {formatDate(edu.endDate)}
                        </Typography>
                        {edu.activities && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, mt: 1.5 }}>
                            {edu.activities.split(',').map((activity) => (
                              <Chip key={activity.trim()} label={activity.trim()} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Languages & Interests */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                {/* Languages */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LanguageIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                        <Typography variant="h5" fontWeight="bold">
                          Languages
                        </Typography>
                      </Stack>
                      <Divider />
                      <Stack spacing={1}>
                        {languages.map((lang, index) => (
                          <Typography key={index} variant="body1">
                            <strong>{lang.name}</strong> - {lang.proficiency}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Community */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Typography variant="h6" fontWeight="bold">
                        Community
                      </Typography>
                      <Divider />
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>GT CoC Alumni Executive Board</strong> - Since 2020
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>GT Executive Round Table</strong> - Member
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          {/* Highlights */}
          <Box id="achievements">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
              <AwardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" fontWeight="bold">
                Notable Achievements
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {[
                { icon: <AwardIcon />, text: 'Enabled ~3× revenue growth by scaling core sales infrastructure at Slabstack' },
                { icon: <WorkIcon />, text: 'Co-founded Triple Axle and raised funding on $250M TAM for specialty trucking' },
                { icon: <CodeIcon />, text: '10x user growth (10K → 100K) at FIXD Automotive in 8 months' },
                { icon: <CodeIcon />, text: 'Reduced publishing costs by 90% with streaming HTML-to-PDF pipeline at Amplify' },
                { icon: <WorkIcon />, text: 'Managed 20% of company traffic via PWA at Glovo (5 languages, 5 cities)' },
                { icon: <AwardIcon />, text: 'Delivered Global Marketplace Engine to 40k+ POCs across Mexico at AB InBev' },
                { icon: <WorkIcon />, text: 'Led technical diligence and integration during acquisition by Sysdyne Technologies' },
                { icon: <CodeIcon />, text: 'Reduced MVP development time by 6 months with AngularJS + React interop at Amplify' },
              ].map((achievement, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Paper
                    sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Box sx={{ color: 'primary.main', flexShrink: 0 }}>
                      {achievement.icon}
                    </Box>
                    <Typography variant="body2">
                      {achievement.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Beyond Code */}
          <Card id="beyond-code" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Typography variant="h4" fontWeight="bold">
                  Beyond Code
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={1}>
                      <MusicNoteIcon sx={{ fontSize: 36 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Music
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Alto saxophone (2nd chair) in GT Jazz Ensemble. Exploring music technology
                        with Spotify API and audio analysis.
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={1}>
                      <SportsIcon sx={{ fontSize: 36 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Sports
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Midfielder on GT Men's Lacrosse. Scholar-Athlete recognition for 2+ varsity sports
                        in high school.
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={1}>
                      <SchoolIcon sx={{ fontSize: 36 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Teaching
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        TA for Ethics of Computing at Georgia Tech. Hosted inaugural international
                        HackGT@UPC in Barcelona.
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
